<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useStore } from 'vuex'
import Header from './component/header.vue'

const store      = useStore()
const permission = computed(() => store.state.getCurrentUserPermission ?? {})

const loading    = ref(false)
const saving     = ref(false)
const accounts   = ref([])
const ledgers    = ref([])
const currencies = ref([])
const showModal  = ref(false)
const formError  = ref('')
const editId     = ref(null)

const blankForm = () => ({
  account_name: '',
  account_number: '',
  bank_name: '',
  account_type: 'current',
  currency_code: 'NGN',
  opening_balance: 0,
  is_default: false,
  ledger_account_id: '',
})
const form = ref(blankForm())

const load = async () => {
  try {
    loading.value = true
    const res = await axios.get('/accounting/bank-accounts')
    accounts.value = res.data.data ?? []
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const loadMeta = async () => {
  try {
    const res = await axios.get('/accounting/active-ledgers')
    const raw = res.data.data ?? {}
    const all = Array.isArray(raw) ? raw : Object.values(raw).flat()
    ledgers.value = all.filter(a => a.type === 'asset')
  } catch (e) {
    console.error('loadMeta failed', e)
  }
}

const loadCurrencies = async () => {
  try {
    const res = await axios.get('/settings/currency')
    currencies.value = res.data.data.data ?? []
  } catch { /* non-critical */ }
}

onMounted(() => { load(); loadMeta(); loadCurrencies() })

const openCreate = () => {
  editId.value = null
  form.value = blankForm()
  formError.value = ''
  showModal.value = true
}

const openEdit = (acc) => {
  editId.value = acc.id
  form.value = {
    account_name: acc.account_name,
    account_number: acc.account_number,
    bank_name: acc.bank_name,
    account_type: acc.account_type,
    currency_code: acc.currency_code,
    opening_balance: acc.opening_balance,
    is_default: acc.is_default,
    ledger_account_id: acc.ledger_account_id ?? '',
  }
  formError.value = ''
  showModal.value = true
}

const save = async () => {
  if (!form.value.account_name || !form.value.account_number || !form.value.bank_name) {
    formError.value = 'Account name, number and bank are required.'
    return
  }
  try {
    saving.value = true
    formError.value = ''
    if (editId.value) {
      await axios.put(`/accounting/bank-accounts/${editId.value}`, form.value)
    } else {
      await axios.post('/accounting/bank-accounts', form.value)
    }
    showModal.value = false
    load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save'
  } finally { saving.value = false }
}

const deleteAccount = async (id) => {
  if (!confirm('Delete this bank account?')) return
  await axios.delete(`/accounting/bank-accounts/${id}`)
  load()
}

const fmt = (v) => Number(v ?? 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })
</script>

<template>
  <div>
    <Header />
    <div class="ba-page">
      <div class="ba-toolbar">
        <h2 class="ba-title">Bank Accounts</h2>
        <button class="btn-primary" @click="openCreate" :disabled="permission.create_bank != 1">+ Add Bank Account</button>
      </div>

      <div v-if="loading" class="ba-loading">Loading…</div>
      <div v-else-if="!accounts.length" class="ba-empty">No bank accounts configured. Add one to get started.</div>
      <div v-else class="ba-grid">
        <div v-for="acc in accounts" :key="acc.id" class="ba-card" :class="acc.is_default ? 'ba-card--default' : ''">
          <div class="ba-card-header">
            <span class="ba-bank">{{ acc.bank_name }}</span>
            <span v-if="acc.is_default" class="ba-default-badge">Default</span>
          </div>
          <div class="ba-acct-name">{{ acc.account_name }}</div>
          <div class="ba-acct-no">{{ acc.account_number }}</div>
          <div class="ba-type">{{ acc.account_type }} · {{ acc.currency_code }}</div>
          <div class="ba-balance">
            <span class="ba-bal-label">Balance</span>
            <span class="ba-bal-val">₦{{ fmt(acc.current_balance) }}</span>
          </div>
          <div v-if="acc.ledger_account" class="ba-ledger">Ledger: {{ acc.ledger_account.code }} – {{ acc.ledger_account.name }}</div>
          <div class="ba-card-actions">
            <button class="btn-link" @click="openEdit(acc)" :disabled="permission.update_bank != 1">Edit</button>
            <button class="btn-link btn-link--red" @click="deleteAccount(acc.id)" :disabled="permission.delete_bank != 1">Delete</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ editId ? 'Edit' : 'Add' }} Bank Account</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group">
              <label>Account Name *</label>
              <input v-model="form.account_name" placeholder="e.g. Main Current Account" />
            </div>
            <div class="form-group">
              <label>Account Number *</label>
              <input v-model="form.account_number" />
            </div>
            <div class="form-group">
              <label>Bank Name *</label>
              <input v-model="form.bank_name" />
            </div>
            <div class="form-group">
              <label>Account Type</label>
              <select v-model="form.account_type">
                <option value="current">Current</option>
                <option value="savings">Savings</option>
                <option value="domiciliary">Domiciliary</option>
              </select>
            </div>
            <div class="form-group">
              <label>Currency</label>
              <select v-model="form.currency_code">
                <option value="" disabled>Choose currency</option>
                <option v-for="c in currencies" :key="c.id" :value="c.code">{{ c.name }} ({{ c.code }})</option>
              </select>
            </div>
            <div class="form-group">
              <label>Opening Balance</label>
              <input type="number" v-model.number="form.opening_balance" min="0" step="0.01" />
            </div>
            <div class="form-group">
              <label>Linked Ledger Account</label>
              <select v-model="form.ledger_account_id">
                <option value="">— None —</option>
                <option v-for="l in ledgers" :key="l.id" :value="l.id">{{ l.code }} – {{ l.name }}</option>
              </select>
            </div>
            <div class="form-group form-group--check">
              <label>
                <input type="checkbox" v-model="form.is_default" />
                Set as default bank account
              </label>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ba-page    { padding: 0 24px 40px; }
.ba-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.ba-title   { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0; }
.ba-loading, .ba-empty { text-align: center; color: #888; padding: 60px; font-size: 14px; }
.ba-grid    { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
.ba-card    { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 18px 20px; transition: box-shadow 0.15s; }
.ba-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
.ba-card--default { border-color: #004AAD; }
.ba-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.ba-bank    { font-size: 13px; color: #888; font-weight: 600; }
.ba-default-badge { background: #004AAD; color: #fff; font-size: 10px; padding: 2px 8px; border-radius: 99px; font-weight: 700; }
.ba-acct-name { font-size: 15px; font-weight: 700; color: #1a2a45; margin-bottom: 3px; }
.ba-acct-no   { font-size: 14px; color: #445; margin-bottom: 4px; font-variant-numeric: tabular-nums; }
.ba-type      { font-size: 12px; color: #888; margin-bottom: 12px; text-transform: capitalize; }
.ba-balance   { display: flex; align-items: baseline; justify-content: space-between; background: #f7f9fc; border-radius: 6px; padding: 10px 14px; margin-bottom: 8px; }
.ba-bal-label { font-size: 12px; color: #888; }
.ba-bal-val   { font-size: 18px; font-weight: 700; color: #1a2a45; }
.ba-ledger    { font-size: 11.5px; color: #888; margin-bottom: 10px; }
.ba-card-actions { display: flex; gap: 8px; border-top: 1px solid #f0f3fa; padding-top: 10px; margin-top: 8px; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; }
.btn-link--red { color: #dc3545; }
.btn-link:disabled, .btn-primary:disabled { opacity: .45; cursor: not-allowed; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 580px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group--check { flex-direction: row; align-items: center; gap: 8px; }
.form-group--check label { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 500; cursor: pointer; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
</style>
